import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Sólo el idioma que el usuario va a ver entra al bundle eager. El otro vive
// en su propio chunk y llega cuando alguien lo pide (Ajustes, o el idioma
// personal/de empresa que resuelve AuthContext después del login).
//
// Por qué: los dos catálogos juntos pesan ~346 kB minificados dentro de un
// eager de 720 kB — casi la mitad de lo que se descarga ANTES de que el
// piloto vea nada, y la mitad de eso es un idioma que no habla. El gate de
// presupuesto (check-bundle-budget.mjs) estaba a 4 kB del techo por esto.
//
// Esto es seguro porque `check-i18n-parity.mjs` garantiza que ambos JSON
// tienen el MISMO juego de claves (4085 hoy). Con paridad, `fallbackLng`
// nunca tiene nada que aportar: si una clave existe en el idioma activo, no
// hace falta el otro catálogo. Si esa paridad se rompiera, el gate de CI se
// pone rojo antes de que llegue a un usuario.
import es from "./es.json";

const SUPPORTED = ["es", "en"];
const FALLBACK = "es";

/**
 * Resuelve el idioma inicial con la misma prioridad que usaba
 * LanguageDetector (localStorage → navigator), pero ANTES de init para poder
 * cargar un solo catálogo. Se mantiene el mismo key de localStorage, así que
 * un usuario que ya eligió idioma no ve ningún cambio.
 */
function detectInitialLanguage() {
  try {
    const saved = localStorage.getItem("altiv_language");
    if (saved && SUPPORTED.includes(saved)) return saved;
  } catch {
    // localStorage puede tronar en modo privado / storage bloqueado.
    // No es motivo para dejar la app sin idioma: seguimos con el navegador.
  }
  const nav = typeof navigator !== "undefined" ? navigator.language || "" : "";
  const base = nav.split("-")[0].toLowerCase();
  return SUPPORTED.includes(base) ? base : FALLBACK;
}

const initialLng = detectInitialLanguage();

// El catálogo español viaja estático porque es el idioma de la mayoría de la
// operación y el fallback. Si el usuario arranca en inglés, el chunk inglés
// se pide de inmediato (abajo) — sigue siendo UNA descarga, no dos.
const resources = { es: { translation: es } };

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: initialLng,
    fallbackLng: FALLBACK,
    supportedLngs: SUPPORTED,
    // Force synchronous init so components don't render before i18n is ready
    initImmediate: false,
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "altiv_language",
    },
    interpolation: {
      escapeValue: false,
    },
  });

// Un `import()` por idioma, con la ruta escrita literal: así Vite puede
// generar el chunk. Un idioma nuevo se agrega en SUPPORTED **y acá** — si
// falta acá, `ensureLanguageLoaded` lo grita en consola en vez de servir el
// catálogo equivocado en silencio.
const LOADERS = {
  en: () => import("./en.json"),
};

/** Catálogos que ya viven en memoria — no se vuelven a pedir. */
const loaded = new Set(["es"]);
/** Descargas en vuelo, para que dos llamadas seguidas compartan una sola. */
const inFlight = new Map();

/**
 * Se asegura de que el catálogo de `lng` esté cargado antes de usarlo.
 * Idempotente y seguro de llamar en cada cambio de idioma.
 *
 * @param {string} lng
 * @returns {Promise<void>}
 */
export function ensureLanguageLoaded(lng) {
  if (!SUPPORTED.includes(lng) || loaded.has(lng)) return Promise.resolve();
  if (inFlight.has(lng)) return inFlight.get(lng);

  const loader = LOADERS[lng];
  if (!loader) {
    console.error(`[i18n] '${lng}' está en SUPPORTED pero no tiene loader en LOADERS`);
    return Promise.resolve();
  }

  const p = loader()
    .then((mod) => {
      i18n.addResourceBundle(lng, "translation", mod.default, true, true);
      loaded.add(lng);
    })
    .catch((err) => {
      // Si el chunk no baja (red caída, deploy nuevo con hash viejo), la app
      // sigue viva en el idioma que ya tiene en vez de quedarse en blanco.
      // Se reporta para no fallar en silencio.
      console.error(`[i18n] no se pudo cargar el catálogo '${lng}'`, err);
    })
    .finally(() => {
      inFlight.delete(lng);
    });

  inFlight.set(lng, p);
  return p;
}

/**
 * Cambia de idioma cargando su catálogo primero. Úsalo en vez de
 * `i18n.changeLanguage` directo: sin el catálogo, la pantalla se llenaría de
 * claves crudas (`settings.language.title`) hasta que llegara el chunk.
 *
 * @param {string} lng
 * @returns {Promise<void>}
 */
export async function changeLanguage(lng) {
  await ensureLanguageLoaded(lng);
  await i18n.changeLanguage(lng);
}

// Si el usuario arranca en un idioma que no viaja estático, lo pedimos ya.
// Hasta que llegue, i18next sirve el fallback — texto real, no claves crudas.
if (initialLng !== FALLBACK) {
  ensureLanguageLoaded(initialLng);
}

export default i18n;
